import { trackEmailComposerAction } from "../../analytics";
import { elementAttributes } from "../html-attributes";
import {
  isButtonCell,
  isTableWrapperCell,
  parseCellBackground,
  parseCellColSpan,
  parseCellWidth,
  parseStyleAttr,
} from "../html-parse";
import {
  EMPTY_STYLE_MAP,
  applyStyleMap,
  paintStyles,
  readStyle,
} from "../shared-block-styles";
import { Node } from "@tiptap/core";
import { Fragment, type Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

import type { CSSProperties } from "react";

import { type CellInfo, collectRowCells } from "./row-utils";

const COLUMN_COUNT = 4;

/**
 * Module-level drag state that survives NodeView recreation.
 * ProseMirror may destroy and recreate NodeViews mid-drag, so keeping
 * state here ensures mousemove/mouseup handlers keep working.
 */
let dragState: {
  cellPos: number;
  startColSpan: number;
  /** Grid column where this cell starts (0-based) */
  cellStartCol: number;
  /** Dragging the left edge of the first cell (inserts cell to the left) */
  leftSide: boolean;
  startX: number;
  editorWidth: number;
  view: EditorView;
  overlay: HTMLElement;
} | null = null;

const cellResizePluginKey = new PluginKey("cellResize");

// ── Span Calculation ──

interface SnapResult {
  span: number;
  startCol: number;
  /** True when the user dragged right-side past zero (wants to delete self) */
  wantsEat: boolean;
}

/**
 * Convert a mouse delta into a snapped column span.
 * Shared by onMouseMove (for visual feedback) and onMouseUp (for commit).
 */
function snapToGrid(
  clientX: number,
  state: {
    startX: number;
    startColSpan: number;
    cellStartCol: number;
    leftSide: boolean;
    editorWidth: number;
  },
): SnapResult {
  const { startX, startColSpan, cellStartCol, leftSide, editorWidth } = state;
  const colW = editorWidth / COLUMN_COUNT;

  if (leftSide) {
    const raw =
      (startColSpan / COLUMN_COUNT) * editorWidth - (clientX - startX);
    const span = Math.max(1, Math.min(COLUMN_COUNT, Math.round(raw / colW)));
    const startCol = cellStartCol + startColSpan - span;
    return { span, startCol, wantsEat: false };
  }

  const raw = (startColSpan / COLUMN_COUNT) * editorWidth + (clientX - startX);
  const maxSpan = COLUMN_COUNT - cellStartCol;
  const rawSnap = Math.round(raw / colW);
  const span = Math.max(1, Math.min(maxSpan, rawSnap));
  return { span, startCol: cellStartCol, wantsEat: rawSnap <= 0 };
}

// ── Overlay (lives outside PM tree → no MutationObserver issues) ──

function createOverlay(rowRect: DOMRect): HTMLElement {
  const overlay = document.createElement("div");
  overlay.style.cssText = `
    position: fixed; z-index: 9999; pointer-events: none;
    left: ${rowRect.left}px; top: ${rowRect.top}px;
    width: ${rowRect.width}px; height: ${rowRect.height}px;
  `;

  // Column guide lines at 25% / 50% / 75%
  for (let i = 1; i < COLUMN_COUNT; i++) {
    const guide = document.createElement("div");
    guide.style.cssText = `
      position: absolute; top: 0; bottom: 0; width: 0;
      left: ${(i / COLUMN_COUNT) * 100}%;
      border-left: 1px dashed rgb(var(--border) / 0.12);
    `;
    overlay.appendChild(guide);
  }

  // Target-width indicator
  const indicator = document.createElement("div");
  indicator.dataset.indicator = "";
  indicator.style.cssText = `
    position: absolute; left: 0; top: 0; bottom: 0;
    background: rgb(var(--primary) / 0.08);
    border-right: 2px solid rgb(var(--primary) / 0.5);
    transition: width 80ms ease-out;
  `;
  overlay.appendChild(indicator);

  document.body.appendChild(overlay);
  return overlay;
}

function updateOverlay(overlay: HTMLElement, startCol: number, span: number) {
  const indicator = overlay.querySelector(
    "[data-indicator]",
  ) as HTMLElement | null;
  if (indicator) {
    indicator.style.left = `${(startCol / COLUMN_COUNT) * 100}%`;
    indicator.style.width = `${(span / COLUMN_COUNT) * 100}%`;
  }
}

// ── Resize Transaction Builder ──

/**
 * Create an empty cell with a single paragraph, for insertion during resize.
 */
function createEmptyCell(
  schema: import("@tiptap/pm/model").Schema,
  colSpan: number,
): PMNode {
  const emptyPara = schema.nodes.emailParagraph.create();
  return schema.nodes.emailCell.create({ colSpan }, Fragment.from(emptyPara));
}

/**
 * Apply a cell resize in either direction. Both left and right resize
 * follow the same three-case pattern:
 *  1. Neighbor exists → adjust both spans (or delete neighbor if squeezed to 0)
 *  2. No neighbor → insert empty cell for remaining space
 *  3. `wantsEat` (right-only) → delete self, give space to neighbor
 */
/**
 * Dragging a column boundary is the user taking the width back from the
 * markup, so the parsed width is cleared and the grid span decides again.
 */
function resizedAttrs(node: PMNode, colSpan: number): Record<string, unknown> {
  return { ...node.attrs, colSpan, width: "" };
}

function applyResize(
  tr: Transaction,
  cells: CellInfo[],
  selfIdx: number,
  cellPos: number,
  cellNode: PMNode,
  newSpan: number,
  startColSpan: number,
  side: "left" | "right",
  wantsEat: boolean,
  cellStartCol: number,
): void {
  const neighborIdx = side === "left" ? selfIdx - 1 : selfIdx + 1;
  const neighbor =
    neighborIdx >= 0 && neighborIdx < cells.length ? cells[neighborIdx] : null;
  const diff = newSpan - startColSpan;

  // Special: user dragged right edge past zero → delete self, expand neighbor
  if (wantsEat && neighbor) {
    tr.delete(cellPos, cellPos + cellNode.nodeSize);
    tr.setNodeMarkup(
      tr.mapping.map(neighbor.pos),
      undefined,
      resizedAttrs(
        neighbor.node,
        (neighbor.node.attrs.colSpan as number) + startColSpan,
      ),
    );
    return;
  }

  if (neighbor) {
    // Neighbor absorbs or releases columns (mirror of our change)
    const neighborNewSpan = (neighbor.node.attrs.colSpan as number) - diff;

    if (neighborNewSpan <= 0) {
      // Neighbor fully consumed → delete it, update self
      tr.delete(neighbor.pos, neighbor.pos + neighbor.node.nodeSize);
      tr.setNodeMarkup(
        tr.mapping.map(cellPos),
        undefined,
        resizedAttrs(cellNode, newSpan),
      );
    } else {
      // Attr-only changes don't shift positions, so order doesn't matter
      tr.setNodeMarkup(cellPos, undefined, resizedAttrs(cellNode, newSpan));
      tr.setNodeMarkup(
        neighbor.pos,
        undefined,
        resizedAttrs(neighbor.node, neighborNewSpan),
      );
    }
    return;
  }

  // No neighbor → update self and insert empty cell for remaining space
  const maxSpace = side === "left" ? startColSpan : COLUMN_COUNT - cellStartCol;
  const remaining = maxSpace - newSpan;

  if (side === "left" && remaining > 0) {
    // Insert before self (shifts cellPos)
    tr.insert(cellPos, createEmptyCell(tr.doc.type.schema, remaining));
    tr.setNodeMarkup(
      tr.mapping.map(cellPos),
      undefined,
      resizedAttrs(cellNode, newSpan),
    );
  } else {
    tr.setNodeMarkup(cellPos, undefined, resizedAttrs(cellNode, newSpan));
    if (remaining > 0) {
      tr.insert(
        cellPos + cellNode.nodeSize,
        createEmptyCell(tr.doc.type.schema, remaining),
      );
    }
  }
}

// ── Plugin ──

function createCellResizePlugin() {
  const onMouseMove = (e: MouseEvent) => {
    if (!dragState) return;
    const { span, startCol } = snapToGrid(e.clientX, dragState);
    updateOverlay(dragState.overlay, startCol, span);
  };

  const onMouseUp = (e: MouseEvent) => {
    if (!dragState) return;
    const { startColSpan, cellStartCol, leftSide, view, cellPos, overlay } =
      dragState;

    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    overlay.remove();

    const { span: newSpan, wantsEat } = snapToGrid(e.clientX, dragState);
    dragState = null;

    if (newSpan === startColSpan && !wantsEat) return;

    const { state } = view;
    const cellNode = state.doc.nodeAt(cellPos);
    if (!cellNode) return;

    const resolvedPos = state.doc.resolve(cellPos);
    const cells = collectRowCells(resolvedPos.parent, resolvedPos.start());
    const selfIdx = cells.findIndex((c) => c.pos === cellPos);
    if (selfIdx === -1) return;

    const tr = state.tr;

    applyResize(
      tr,
      cells,
      selfIdx,
      cellPos,
      cellNode,
      newSpan,
      startColSpan,
      leftSide ? "left" : "right",
      wantsEat,
      cellStartCol,
    );

    if (tr.steps.length > 0) {
      view.dispatch(tr);

      trackEmailComposerAction("cell_resized", "resize", {
        from_col_span: startColSpan,
        to_col_span: newSpan,
      });
    }
  };

  return new Plugin({
    key: cellResizePluginKey,
    props: {
      handleDOMEvents: {
        mousedown(view, event) {
          const target = event.target as HTMLElement;
          const isRight = target.classList.contains("email-cell-resize-handle");
          const isLeft = target.classList.contains(
            "email-cell-resize-handle-left",
          );
          if (!isRight && !isLeft) return false;

          event.preventDefault();

          const cellDom = target.closest(".email-cell") as HTMLElement | null;
          if (!cellDom) return true;
          const rowDom = cellDom.closest(".email-row") as HTMLElement | null;
          if (!rowDom) return true;

          // Resolve cell position in the document
          const pos = view.posAtDOM(cellDom, 0);
          const resolvedPos = view.state.doc.resolve(pos);

          let clickedCellPos: number | null = null;
          for (let d = resolvedPos.depth; d >= 0; d--) {
            if (resolvedPos.node(d).type.name === "emailCell") {
              clickedCellPos = resolvedPos.before(d);
              break;
            }
          }
          if (clickedCellPos === null) return true;

          // Collect all cells in the row with their starting columns
          const clickedResolved = view.state.doc.resolve(clickedCellPos);
          const rowNode = clickedResolved.parent;
          const rowStart = clickedResolved.start();

          const cells: Array<{
            pos: number;
            colSpan: number;
            startCol: number;
          }> = [];
          let col = 0;
          rowNode.forEach((child, offset) => {
            cells.push({
              pos: rowStart + offset,
              colSpan: child.attrs.colSpan as number,
              startCol: col,
            });
            col += child.attrs.colSpan as number;
          });

          const clickedIdx = cells.findIndex((c) => c.pos === clickedCellPos);
          if (clickedIdx === -1) return true;

          // For left handle, resize the PREVIOUS cell (the boundary is shared).
          // If there's no previous cell (first cell), we treat it as a
          // "left-side resize" of the clicked cell itself.
          let targetIdx: number;
          let leftSide = false;
          if (isLeft) {
            if (clickedIdx === 0) {
              targetIdx = 0;
              leftSide = true;
            } else {
              targetIdx = clickedIdx - 1;
            }
          } else {
            targetIdx = clickedIdx;
          }

          const targetCell = cells[targetIdx];
          const targetNode = view.state.doc.nodeAt(targetCell.pos);
          if (!targetNode) return true;

          const rowRect = rowDom.getBoundingClientRect();
          const overlay = createOverlay(rowRect);
          updateOverlay(overlay, targetCell.startCol, targetCell.colSpan);

          dragState = {
            cellPos: targetCell.pos,
            startColSpan: targetCell.colSpan,
            cellStartCol: targetCell.startCol,
            leftSide,
            startX: event.clientX,
            editorWidth: rowRect.width,
            view,
            overlay,
          };

          document.addEventListener("mousemove", onMouseMove);
          document.addEventListener("mouseup", onMouseUp);

          return true;
        },
      },
    },
  });
}

/**
 * emailCell node — a cell within an emailRow that spans 1-4 columns
 * of the 4-column grid. Width is `(colSpan/4) * 100%`.
 *
 * Uses CSS `display: table-cell` in the editor to match the email output's
 * `<td>` structure. Resize is handled by a ProseMirror plugin (not the
 * NodeView) so drag state survives NodeView recreation. Visual feedback
 * during drag uses a fixed-position overlay outside the PM tree to avoid
 * MutationObserver-triggered re-renders.
 */
/**
 * Attributes the cell already models. Storing them again would mean emitting
 * them twice, and on a reopened draft accumulating them.
 */
const MODELLED_CELL_ATTRIBUTES = [
  "colspan",
  "width",
  "bgcolor",
  "data-cell",
  "data-col-span",
  "data-width",
  "data-background-color",
  "data-padding",
];

export const EmailCell = Node.create({
  name: "emailCell",
  // emailRow is allowed inside a cell so a genuine side-by-side layout nested in
  // a designed email keeps its columns. Pure wrapper tables are declined at
  // parse time (see isTableWrapperCell), so this nesting only appears where the
  // design actually has columns within a section.
  content: "(emailBlock | emailRow | bulletList | orderedList)+",
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      colSpan: {
        default: COLUMN_COUNT,
        parseHTML: (element: HTMLElement) => {
          // Read from data-col-span (always rendered), fallback to colSpan attribute
          const dataVal = element.getAttribute("data-col-span");
          if (dataVal !== null) return Number(dataVal);
          // A real table cell counts columns in its own table, not in our grid,
          // so its share of the row is what maps across (see parseCellColSpan).
          const tag = element.tagName.toLowerCase();
          if (tag === "td" || tag === "th") return parseCellColSpan(element);
          const htmlVal = element.getAttribute("colSpan");
          if (htmlVal !== null) return Number(htmlVal);
          return undefined;
        },
      },
      /**
       * The cell's own width, when the markup gave it one.
       *
       * The editor's four-column grid cannot express a third of a row, so a
       * cell parsed from a real table carries its true share here and the grid
       * span is only what the resize handles work with. Empty means "however
       * wide the grid says", which is every cell the composer created itself.
       */
      width: {
        default: "",
        parseHTML: (element: HTMLElement) => {
          const stored = element.getAttribute("data-width");
          if (stored !== null) return stored;
          const tag = element.tagName.toLowerCase();
          if (tag !== "td" && tag !== "th") return "";
          return parseCellWidth(element);
        },
      },
      /** A band painted on the cell rather than on the surrounding table. */
      backgroundColor: {
        default: "",
        parseHTML: (element: HTMLElement) => {
          const stored = element.getAttribute("data-background-color");
          if (stored !== null) return stored;
          return parseCellBackground(element);
        },
      },
      /**
       * A designed email does its spacing with cell padding; dropping it
       * collapses the band onto the text it was meant to frame.
       */
      padding: {
        default: "",
        parseHTML: (element: HTMLElement) => {
          const stored = element.getAttribute("data-padding");
          if (stored !== null) return stored;
          return element.style.padding || "";
        },
      },
      /**
       * Everything else the cell was written with.
       *
       * A designed email centres a band with `align="center"` on the `td`, not
       * with CSS, and names the cell so a media query can reach it. Modelling
       * five attributes and dropping the rest broke the invariant the typed
       * blocks are supposed to hold: claim an element only if it can be
       * reproduced. This is the same store `htmlBlock` keeps.
       */
      attributes: {
        default: {},
        parseHTML: (element: HTMLElement) =>
          elementAttributes(element, MODELLED_CELL_ATTRIBUTES),
        // Carried in the node's JSON, never re-rendered onto the editor's own
        // markup — the send renderer is what puts these back on a real `td`.
        renderHTML: () => ({}),
      },

      /** The `td`'s own inline styles, as written. */
      style: {
        default: EMPTY_STYLE_MAP,
        parseHTML: (element: HTMLElement) => parseStyleAttr(element),
        // Carried in the node's JSON, never as an HTML attribute — the
        // element's own `style` is built from the grid's own values below.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-cell="true"]', priority: 100 },
      ...["td", "th"].map((tag) => ({
        tag,
        // A cell that only holds a nested table, or that only exists to fill a
        // button, is scaffolding — decline it so its contents parse in place.
        getAttrs: (node: HTMLElement | string) => {
          const el = node as HTMLElement;
          if (isTableWrapperCell(el) || isButtonCell(el)) return false;
          return null;
        },
      })),
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const colSpan = node.attrs.colSpan as number;
    const widthPercent = (colSpan / COLUMN_COUNT) * 100;
    const backgroundColor = node.attrs.backgroundColor as string;
    const padding = readStyle(node.attrs, "padding");
    const width = (node.attrs.width as string) || `${widthPercent}%`;
    return [
      "div",
      {
        ...HTMLAttributes,
        "data-cell": "true",
        "data-col-span": String(colSpan),
        ...((node.attrs.width as string) ? { "data-width": width } : {}),
        ...(backgroundColor
          ? { "data-background-color": backgroundColor }
          : {}),
        ...(padding ? { "data-padding": padding } : {}),
        style: [
          `width: ${width}`,
          backgroundColor ? `background-color: ${backgroundColor}` : "",
          padding ? `padding: ${padding}` : "",
        ]
          .filter(Boolean)
          .join("; "),
      },
      0,
    ];
  },

  addProseMirrorPlugins() {
    return [createCellResizePlugin()];
  },

  addNodeView() {
    return ({ node: initialNode, HTMLAttributes }) => {
      let node = initialNode;
      const dom = document.createElement("div");
      dom.setAttribute("data-cell", "true");
      dom.className = "email-cell";

      for (const [key, value] of Object.entries(HTMLAttributes)) {
        if (typeof value === "string") {
          dom.setAttribute(key, value);
        }
      }

      const contentDOM = document.createElement("div");
      contentDOM.className = "email-cell-content";
      dom.appendChild(contentDOM);

      // Resize handles — visual only; drag logic is in the plugin
      const resizeHandleLeft = document.createElement("div");
      resizeHandleLeft.className = "email-cell-resize-handle-left";
      dom.appendChild(resizeHandleLeft);

      const resizeHandle = document.createElement("div");
      resizeHandle.className = "email-cell-resize-handle";
      dom.appendChild(resizeHandle);

      const updateWidth = (updatedNode: typeof node) => {
        const colSpan = updatedNode.attrs.colSpan as number;
        const declared = (updatedNode.attrs.width as string) || "";
        dom.style.width = declared || `${(colSpan / COLUMN_COUNT) * 100}%`;
        dom.setAttribute("data-col-span", String(colSpan));
      };
      // Show the cell's own design in the editor, so the composer matches what
      // will be sent. Everything the markup declared goes on first, then the
      // values the grid owns — the same order the send renderer uses.
      let painted = new Set<string>();
      const updateStyling = (updatedNode: typeof node) => {
        const styles: CSSProperties = {};
        applyStyleMap(styles, updatedNode.attrs);

        // A designed email centres a band with `align="center"` on the `td`.
        // The canvas cell is a `div`, where the same instruction is spelled
        // `text-align` — without it the composer shows the design flush left
        // while the inbox shows it centred.
        const stored = (updatedNode.attrs.attributes ?? {}) as Record<
          string,
          string
        >;
        if (stored.align)
          styles.textAlign = stored.align as CSSProperties["textAlign"];

        const backgroundColor = updatedNode.attrs.backgroundColor as string;
        if (backgroundColor) styles.backgroundColor = backgroundColor;
        const padding = readStyle(updatedNode.attrs, "padding");
        if (padding) styles.padding = padding;
        painted = paintStyles(dom, styles, painted);
      };
      updateStyling(node);
      updateWidth(node);

      return {
        dom,
        contentDOM,
        update(updatedNode) {
          if (updatedNode.type.name !== "emailCell") return false;
          updateStyling(updatedNode);
          updateWidth(updatedNode);
          node = updatedNode;
          return true;
        },
      };
    };
  },
});
